import { styled } from '@mui/material/styles';
import { Box, Card, Typography } from '@mui/material';

const honeyColors = {
    primary: '#F1C40F',
    primaryLight: '#F4D03F',
    primaryDark: '#D4AC0D',
    amber: '#FFB74D',
    cream: '#FFF8E1',
    textPrimary: '#3E2723',
    textSecondary: '#5D4037',
    gradientPrimary: 'linear-gradient(135deg, #F1C40F 0%, #FFB74D 100%)',
    shadowLight: '0 2px 8px rgba(212, 165, 116, 0.15)',
    shadowMedium: '0 4px 16px rgba(212, 165, 116, 0.25)',
};

export const EventsContainer = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

export const EventsHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

export const EventsGrid = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
    },
}));

export const EventCard = styled(Card)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    height: '120px',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    overflow: 'visible', // Allow badge to overflow
    borderRadius: '12px',
    border: `1px solid ${honeyColors.primaryLight}30`,
    position: 'relative',

    '&:hover': {
        boxShadow: honeyColors.shadowMedium,
        transform: 'translateY(-2px)',
        borderColor: honeyColors.primary,
    },
}));

// Floating badge for "Booked", "Attending", or "Join"
export const AttendingBadge = styled(Box)(({ variant }) => {
    let backgroundColor, color;
    
    if (variant === 'owner') {
        // User created this booking - green
        backgroundColor = '#4CAF50';
        color = '#fff';
    } else if (variant === 'attending') {
        // User joined this booking - green
        backgroundColor = '#4CAF50';
        color = '#fff';
    } else if (variant === 'join') {
        // User can join - yellow/honey
        backgroundColor = '#FFC107';
        color = '#000';
    } else {
        backgroundColor = '#FFC107';
        color = '#000';
    }
    
    return {
        position: 'absolute',
        top: -8,
        right: -8,
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '0.65rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 1,
        backgroundColor,
        color,
        transition: 'transform 0.15s ease-in-out',
    };
});

export const EventDateBox = styled(Box)({
    width: '80px',
    minWidth: '80px',
    background: honeyColors.gradientPrimary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: honeyColors.textPrimary,
    padding: '8px',

    '& .day': {
        fontSize: '1.6rem',
        fontWeight: 800,
        lineHeight: 1,
    },

    '& .month': {
        fontSize: '0.8rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        marginTop: '2px',
    },

    '& .weekday': {
        fontSize: '0.65rem',
        fontWeight: 500,
        opacity: 0.8,
        marginTop: '2px',
    },

    '& .time': {
        fontSize: '0.7rem',
        fontWeight: 600,
        marginTop: '4px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        padding: '2px 6px',
        borderRadius: '4px',
    },
});

export const EventContent = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    overflow: 'hidden',
}));

export const EventTitle = styled(Typography)({
    fontWeight: 600,
    fontSize: '0.9rem',
    color: honeyColors.textPrimary,
    lineHeight: 1.3,
    display: '-webkit-box',
    WebkitLineClamp: 1, // Single line with ellipsis
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginBottom: '4px',
    minHeight: '1.3em', // Fixed height for 1 line
    maxHeight: '1.3em',
});

export const EventMeta = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',

    '& .meta-item': {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.8rem',
        color: honeyColors.textSecondary,

        '& svg': {
            fontSize: '0.9rem',
            color: honeyColors.primaryDark,
        },
    },
});

export const EventTypeBadge = styled(Box)(({ eventType }) => {
    let background, color;

    if (eventType === 'online') {
        background = honeyColors.cream;
        color = honeyColors.caramel || honeyColors.primaryDark;
    } else if (eventType === 'community-booking') {
        background = '#E8F5E9';
        color = '#2E7D32';
    } else {
        background = honeyColors.cream;
        color = honeyColors.primaryDark;
    }
    
    return {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '6px',
        fontSize: '0.7rem',
        fontWeight: 600,
        background,
        color,
        alignSelf: 'flex-start',
        marginTop: '4px',
    };
});

export const EmptyEventsBox = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(4),
    background: honeyColors.cream,
    borderRadius: '16px',
    border: `2px dashed ${honeyColors.primaryLight}`,

    '& svg': {
        fontSize: '48px',
        color: honeyColors.primary,
        marginBottom: theme.spacing(1),
    },

    '& .title': {
        fontWeight: 600,
        color: honeyColors.textPrimary,
        marginBottom: theme.spacing(0.5),
    },

    '& .subtitle': {
        fontSize: '0.9rem',
        color: honeyColors.textSecondary,
    },
}));
